import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TooltipHelp } from "@/components/ui/TooltipHelp";
import { useNoteLanguage } from "@/hooks/user/useNoteLanguage";
import { noteLanguageForInput } from "@/lib/note-language";

const SAME = "same";

// "Note language" in the New visit sheet, under the input language. Saves the
// same account preference as Settings → Transcription, so the choice is
// remembered for the account.
export const NoteLanguageSelect = ({
  inputLanguage,
}: {
  inputLanguage: string | undefined;
}): JSX.Element => {
  const { t } = useTranslation();
  const {
    options,
    current,
    sameAsConversation,
    loading,
    saving,
    failed,
    chooseLanguage,
    chooseSameAsConversation,
    syncWithInput,
  } = useNoteLanguage();

  // While "Same as conversation" is on, follow the input language.
  const synced = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!sameAsConversation || !options.length) return;
    if (synced.current === inputLanguage) return;
    synced.current = inputLanguage;
    void syncWithInput(inputLanguage);
  }, [sameAsConversation, inputLanguage, options.length, syncWithInput]);

  const inputMatch = noteLanguageForInput(inputLanguage, options);
  const autoDetect = !inputLanguage || inputLanguage === "auto";
  const translating =
    !sameAsConversation &&
    current &&
    inputMatch &&
    inputMatch.id !== current.id;

  let hint: string | null = null;
  if (failed) hint = t("noteLanguage.saveFailed");
  else if (sameAsConversation && autoDetect && current)
    hint = t("noteLanguage.autoDetectHint", { language: current.inline });
  else if (translating)
    hint = t("noteLanguage.translateHint", { language: current.inline });

  return (
    <div data-testid="note-language">
      <Label className="text-tinted-grey-3" htmlFor="note-language-select">
        {t("noteLanguage.label")}
      </Label>
      <div className="flex items-center gap-4">
        <Select
          disabled={loading || saving || !options.length}
          value={sameAsConversation ? SAME : current ? String(current.id) : ""}
          onValueChange={(value) => {
            if (value === SAME) void chooseSameAsConversation(inputLanguage);
            else void chooseLanguage(Number(value));
          }}
        >
          <SelectTrigger
            aria-describedby={hint ? "note-language-hint" : undefined}
            className="min-w-0 flex-1 focus:ring-0 [&>span]:truncate"
            id="note-language-select"
          >
            <SelectValue placeholder={t("noteLanguage.placeholder")}>
              {sameAsConversation
                ? current
                  ? t("noteLanguage.sameAsConversationWith", {
                      language: current.inline,
                    })
                  : t("noteLanguage.sameAsConversation")
                : current?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SAME}>
              {t("noteLanguage.sameAsConversation")}
            </SelectItem>
            <SelectSeparator />
            {options.map((option) => (
              <SelectItem key={option.id} value={String(option.id)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <TooltipHelp content={t("noteLanguage.tooltip")} />
      </div>
      {hint && (
        <p
          className={
            failed
              ? "mt-1 text-xs text-destructive"
              : "mt-1 text-xs text-muted-foreground"
          }
          id="note-language-hint"
          role={failed ? "alert" : undefined}
        >
          {hint}
        </p>
      )}
    </div>
  );
};
